import useUpload from "@/hooks/useUpload";
import ProgressBar from "./ProgressBar";
import { useState } from "react";
import { getFileName } from "../helpers";
import { Check, Eye, Trash2 } from "lucide-react";
import { UploadedFile as UploadedFileType } from "@/api/lctApISchemas";
import { FilePreview } from "./FilePreview";
import { useAppSelector } from "@/hooks/reduxHooks";
import { IconButton } from "@/components/IconButton";
import { useUserAppInfo } from "@/hooks/useUserAppInfo";

interface UploadedFileProps {
  file: UploadedFileType;
  userAppId: string;
  onFileDelete?: (file: UploadedFileType) => void;
  questionId?: string;
}

export function UploadedFile({ file, userAppId, onFileDelete, questionId }: UploadedFileProps) {
  const { uploading, fileUploadProgress } = useUpload(userAppId, questionId);
  const [fileToPreview, setFileToPreview] = useState<UploadedFileType | null>(null);
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const { applicationEditable } = useUserAppInfo(userAppId);

  const fileName = getFileName(file);
  const progress = fileUploadProgress?.progress ?? 0;

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center justify-between gap-1 rounded-sm border border-solid border-green-400 bg-green-50 px-4 py-6">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <Check size={18} className="flex-shrink-0 text-green-900" />
          <p className="truncate text-sm">{fileName}</p>
        </div>
        <IconButton variant="outline" size="icon" className="text-slate-900" onClick={() => setFileToPreview(file)} tooltip="Preview">
          <Eye size={20} />
        </IconButton>
        {!isStaff && applicationEditable ? (
          <IconButton
            variant="outline"
            size="icon"
            onClick={() => {
              onFileDelete?.(file);
            }}
            className="cursor-pointer items-center justify-center px-2 text-error transition-all group-hover:flex"
            disabled={uploading}
            tooltip="Delete"
          >
            <Trash2 size={16} />
          </IconButton>
        ) : null}
      </div>

      {fileToPreview ? <FilePreview file={file} isOpen={!!file} onOpenChange={(open) => !open && setFileToPreview(null)} /> : null}

      {uploading && (
        <div className="mt-2">
          <ProgressBar progress={progress} />
        </div>
      )}
    </div>
  );
}
